import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import passwordValidator from 'password-validator';
import {
  showToast,
  validateConfirmPassword
} from '../../../utils/user/patient/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { Eye, EyeOff } from 'react-feather';
import { hospitalConfirmPasswordReset } from '../../../redux/middleware/user/hospital/resetPassword';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// Password validation schema
const passwordValidationSchema = new passwordValidator();
passwordValidationSchema
  .is()
  .min(8) // Minimum length of 8
  .has()
  .letters() // Must include letters
  .has()
  .digits() // Must include digits
  .has()
  .symbols() // Must include special characters
  .has()
  .not()
  .spaces(); // Should not contain spaces

// Yup validation schema for Reset Password
const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('New password is required')
    .test(
      'isStrongPassword',
      'Password must include at least 8 characters, one number, and one special character',
      (value) => passwordValidationSchema.validate(value)
    ),
  confirmPassword: Yup.string().required('Confirm password is required')
});

const HospitalResetPassword = ({ toast }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, successMessage, error } = useSelector(
    (state) => state.hospitalPassword || {}
  );
  const validLink = resetToken !== null;

  // Extract the reset token from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get('resetToken');
  // State for the password fields
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const handleResetChange = (e) => {
    setResetPassword({ ...resetPassword, [e.target.name]: e.target.value });
  };

  // Handle Password Reset Submission
  const handlePasswordReset = async () => {
    event.preventDefault();
    if (resetPassword.newPassword !== resetPassword.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    try {
      await dispatch(
        hospitalConfirmPasswordReset(
          resetToken,
          resetPassword.newPassword,
          resetPassword.confirmPassword
        )
      );
      toast.success('Password reset successful! Please log in.');
      setIsResetModalOpen(false);
      navigate('/app/hospital/login');
    } catch (error) {
      toast.error('Failed to reset password. Please try again.');
    }
  };

  return (
    <div>
      {validLink
        ? (
          <form onSubmit={handlePasswordReset} className={css(styles.form)}>
            <motion.div
              className={css(styles.inputGroup)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <div className={css(styles.passwordInputWrapper)}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name='newPassword'
                  placeholder='New Password'
                  value={resetPassword.newPassword}
                  onChange={handleResetChange}
                  className={css(styles.input, styles.passwordInput)}
                />
                <button
                  type='button'
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className={css(styles.passwordToggle)}
                >
                  {showNewPassword
                    ? (
                    <EyeOff className={css(styles.eyeIcon)} />
                      )
                    : (
                    <Eye className={css(styles.eyeIcon)} />
                      )}
                </button>
              </div>
            </motion.div>
            <motion.div
              className={css(styles.inputGroup)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <div className={css(styles.passwordInputWrapper)}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name='confirmPassword'
                  placeholder='Confirm Password'
                  value={resetPassword.confirmPassword}
                  onChange={handleResetChange}
                  className={css(styles.input, styles.passwordInput)}
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className={css(styles.passwordToggle)}
                >
                  {showConfirmPassword
                    ? (
                    <EyeOff className={css(styles.eyeIcon)} />
                      )
                    : (
                    <Eye className={css(styles.eyeIcon)} />
                      )}
                </button>
              </div>
            </motion.div>
            <motion.button
              type='submit'
              className={css(styles.button)}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              {loading
                ? (
                  <CircularProgress size={24} color='inherit' />
                  )
                : (
						  'Confirm Password'
                  )}
            </motion.button>
          </form>
          )
        : (
          <motion.div
            className={css(styles.warning)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            Invalid or expired reset link.
          </motion.div>
          )}
    </div>
  );
};

const styles = StyleSheet.create({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  inputGroup: {
    position: 'relative'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.3s',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)'
    }
  },
  passwordInputWrapper: {
    position: 'relative',
    width: '100%'
  },
  passwordInput: {
    paddingRight: '40px'
  },
  passwordToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#4a5568'
  },
  eyeIcon: {
    color: 'black',
    width: '1.15rem',
    height: '1.15rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#1c5fa8'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  error: {
    color: '#e53e3e',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  warning: {
    color: '#e53e3e',
    fontSize: '1rem',
    textAlign: 'center',
    marginTop: '1rem'
  }
});

export default HospitalResetPassword;
